import { motion } from "framer-motion";
import { useEffect } from "react";
import { Target, Eye, Search, Compass, Blocks, RefreshCw } from "lucide-react";
import { MarketingHeader, MarketingFooter, BookCTA } from "@/components/marketing";

const TEAL = "#22D3C5";
const EASE = [0.22, 1, 0.36, 1];

const FOUNDERS = [
  { name: "Daniel Ravn", role: "Marketingprofil & medstifter", email: "dr@aicom.dk", img: "/founders/daniel.jpg" },
  { name: "Rasmus Kjær", role: "Marketingprofil & medstifter", email: "rk@aicom.dk", img: "/founders/rasmus.jpg" },
];

const PROCESS = [
  { n: "01", title: "Vi forstår udfordringen", desc: "Vi sætter os ind i jeres forretning, behov og den opgave, I vil løse.", Icon: Search },
  { n: "02", title: "Vi former løsningen", desc: "Vi finder det rigtige greb og designer en løsning, der passer til jer.", Icon: Compass },
  { n: "03", title: "Vi bygger og implementerer", desc: "Vi omsætter idéen til noget, der fungerer i praksis.", Icon: Blocks },
  { n: "04", title: "Vi udvikler videre", desc: "Vi følger brugen, lærer af resultaterne og forbedrer løsningen løbende.", Icon: RefreshCw },
];

export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero — editorial */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-16">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
              <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TEAL }} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Om Aicom</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
            className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance max-w-3xl">
            Knivskarp kommunikation, sat i system med AI
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: EASE }}
            className="mt-6 space-y-4 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
            <p>Aicom er stiftet af erfarne marketingfolk og ejet af det kreative bureau <span className="text-foreground font-medium">PointTaken</span>, der siden 2017 har arbejdet med kommunikationsstrategier, digital markedsføring, content og PR.</p>
            <p>Det betyder, at erfarne konsulenter - i tæt samarbejde med jer - laver strategien og skaber rammerne for AI-teknologierne. Vi bruger AI til at sætte fart på jeres kommunikation og markedsføring, og vi kvalitetssikrer resultaterne.</p>
          </motion.div>
        </div>
      </section>

      {/* Mennesker bag */}
      <section className="relative z-10 border-y border-border bg-secondary/50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-[0.4fr_0.6fr] gap-10 lg:gap-16">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Menneskene bag</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Bag Aicom står to marketingprofiler</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed max-w-md">Daniel Ravn &amp; Rasmus Kjær - med rødder i bureauverdenen og en fælles ambition om at gøre stærk kommunikation tilgængelig for flere.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-6">
            {FOUNDERS.map((f, i) => (
              <div key={f.name} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`founder-${i}`}>
                <div className="aspect-square overflow-hidden bg-secondary">
                  <img src={f.img} alt={f.name} className="w-full h-full object-cover grayscale" loading="eager" fetchpriority="high" decoding="sync" width="640" height="640" />
                </div>
                <div className="p-5">
                  <p className="font-display text-lg font-medium">{f.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">{f.role}</p>
                  <a href={`mailto:${f.email}`} className="inline-block text-sm text-foreground underline underline-offset-2 hover:no-underline mt-2 break-all" data-testid={`founder-email-${i}`}>{f.email}</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sådan arbejder Aicom */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl mb-14">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Sådan arbejder Aicom</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Fra udfordring til løsning, der skaber værdi</h2>
          </div>
          <div className="hidden md:block relative">
            <div className="absolute top-5 left-0 right-0 h-px bg-border" />
            <div className="relative grid grid-cols-4 gap-6">
              {PROCESS.map((f, i) => (
                <motion.div key={f.n} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.45, delay: i * 0.08, ease: EASE }} className="flex flex-col items-start">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card relative z-10">
                    <f.Icon className="h-[18px] w-[18px]" style={{ color: TEAL }} strokeWidth={1.75} />
                  </span>
                  <p className="text-[11px] tracking-wide text-muted-foreground/70 mt-5">{f.n}</p>
                  <p className="font-display text-lg font-medium mt-1">{f.title}</p>
                  <p className="text-[13.5px] text-muted-foreground mt-2 leading-relaxed max-w-[15rem]">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="md:hidden relative pl-8">
            <div className="absolute left-[19px] top-2 bottom-2 w-px bg-border" />
            <div className="space-y-8">
              {PROCESS.map((f) => (
                <div key={f.n} className="relative flex items-start gap-4">
                  <span className="absolute -left-8 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card">
                    <f.Icon className="h-[18px] w-[18px]" style={{ color: TEAL }} strokeWidth={1.75} />
                  </span>
                  <div className="ml-4">
                    <p className="text-[11px] tracking-wide text-muted-foreground/70">{f.n}</p>
                    <p className="font-display text-lg font-medium">{f.title}</p>
                    <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="relative z-10 border-t border-border bg-secondary/50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-border bg-card p-8" data-testid="about-mission" style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}>
            <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center mb-5">
              <Target className="h-5 w-5" style={{ color: TEAL }} strokeWidth={1.75} />
            </div>
            <h2 className="font-display text-xl font-medium">Vores mission</h2>
            <p className="text-muted-foreground mt-3 leading-relaxed">At hjælpe vores kunder med AI-drevet markedsføring og kommunikation, så de får en markedsføringskapacitet, der før var forbeholdt de største virksomheder med selvstændige kommunikationsafdelinger og enorme budgetter.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-8" data-testid="about-vision" style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}>
            <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center mb-5">
              <Eye className="h-5 w-5" style={{ color: TEAL }} strokeWidth={1.75} />
            </div>
            <h2 className="font-display text-xl font-medium">Vores vision</h2>
            <p className="text-muted-foreground mt-3 leading-relaxed">At hjælpe vores kunder med at realisere deres fulde potentiale ved at bruge state of the art AI-teknologi, hvor vejen fra idé til resultat er kortere end nogensinde før.</p>
          </div>
        </div>
      </section>

      <BookCTA />
      <MarketingFooter />
    </div>
  );
}
